import { ScaleType, RootNote, ChordQuality } from '../../types';

// V11 Enhancement: Allow specifying quality and borrowing
export interface ProgressionChord {
    degree: number; // 0-6
    quality?: ChordQuality; // Override calculated quality
    isBorrowed?: boolean; // Force borrowed behavior (e.g. for modal interchange)
}

export type ProgressionStep = number | ProgressionChord;

/**
 * Curated chord progressions that always sound good.
 * Degrees are 0-indexed scale degrees (0=I, 1=ii, 2=iii, 3=IV, 4=V, 5=vi, 6=vii)
 */
export interface ComposerProgression {
    name: string;
    degrees: ProgressionStep[];
    moods: ('Happy' | 'Sad' | 'Energetic' | 'Calm' | 'Mystery' | 'Any' | 'Nostalgic' | 'Emotional' | 'Epic' | 'Exotic')[];
    complexity: 'Simple' | 'Medium' | 'Complex' | 'Exotic';
}

/**
 * A massive library of chord progressions across various genres.
 * Simple = Foundations, Medium = Jazz/R&B Staples, Complex = Neo-Soul/Lo-Fi, Exotic = Cinematic/Modal
 */
export const COMPOSER_PROGRESSIONS: ComposerProgression[] = [
    // --- SIMPLE (Foundational Pop/Folk) ---
    { name: 'Pop Anthem', degrees: [0, 4, 5, 3], moods: ['Happy', 'Energetic', 'Any'], complexity: 'Simple' },      // I-V-vi-IV
    { name: 'Classic', degrees: [0, 3, 4, 0], moods: ['Happy', 'Calm'], complexity: 'Simple' },         // I-IV-V-I
    { name: 'Golden Loop', degrees: [0, 4, 3, 0], moods: ['Happy', 'Energetic'], complexity: 'Simple' }, // I-V-IV-I (The Golden 1-4-5)
    { name: 'Simple Answer', degrees: [0, 4, 0], moods: ['Calm', 'Happy'], complexity: 'Simple' },      // I-V-I (Question & Answer)
    { name: 'Sad Pop', degrees: [5, 3, 0, 4], moods: ['Sad', 'Mystery'], complexity: 'Simple' },         // vi-IV-I-V
    { name: 'Folk', degrees: [0, 5, 1, 4], moods: ['Calm', 'Happy'], complexity: 'Simple' },            // I-vi-ii-V
    { name: '50s Ballad', degrees: [0, 5, 3, 4], moods: ['Happy', 'Nostalgic'], complexity: 'Simple' },   // I-vi-IV-V
    { name: 'Canon', degrees: [0, 4, 5, 2, 3, 0, 3, 4], moods: ['Calm', 'Happy'], complexity: 'Simple' }, // Pachelbel-ish

    // --- MEDIUM (Jazz, R&B, Emotional Staples) ---
    { name: 'Jazz 2-5-1', degrees: [1, 4, 0], moods: ['Calm', 'Happy', 'Any'], complexity: 'Medium' }, // ii-V-I
    { name: 'Jazz Turnaround', degrees: [0, 5, 1, 4], moods: ['Energetic', 'Happy'], complexity: 'Medium' }, // I-vi-ii-V
    { name: 'Royal Road', degrees: [3, 4, 2, 5], moods: ['Emotional', 'Sad'], complexity: 'Medium' }, // IV-V-iii-vi (Anime/J-Pop staple)
    { name: 'Circle of Fifths', degrees: [2, 5, 1, 4, 0], moods: ['Energetic', 'Happy'], complexity: 'Medium' }, // iii-vi-ii-V-I
    { name: 'Emotional Journey', degrees: [5, 3, 0, 4, 1, 5, 3, 4], moods: ['Sad', 'Emotional', 'Epic'], complexity: 'Medium' },
    { name: 'Rising Spirit', degrees: [0, 1, 2, 3], moods: ['Happy', 'Energetic'], complexity: 'Medium' }, // I-ii-iii-IV linear rise
    { name: 'Descending Soul', degrees: [3, 2, 1, 0], moods: ['Calm', 'Sad'], complexity: 'Medium' }, // IV-iii-ii-I
    { name: 'Andalusian', degrees: [0, 6, 5, 4], moods: ['Energetic', 'Mystery', 'Exotic'], complexity: 'Medium' }, // i-VII-VI-V (Minor)

    // --- COMPLEX (Neo-Soul, Lo-Fi, Advanced Jazz) ---
    // Requires handling borrowed chords and specific qualities
    {
        name: 'Neo Soul Vibes',
        degrees: [3, { degree: 2, quality: 'dominant7', isBorrowed: true }, 5, { degree: 4, quality: 'minor7', isBorrowed: true }],
        moods: ['Calm', 'Mystery'],
        complexity: 'Complex'
    }, // IVmaj7 - III7 - vi7 - v7 (Just the Two of Us vibe)

    {
        name: 'Lo-Fi Nostalgia',
        degrees: [1, 4, 0, { degree: 5, quality: 'dominant7', isBorrowed: true }],
        moods: ['Calm', 'Sad', 'Nostalgic'],
        complexity: 'Complex'
    }, // ii-V-I-VI7 (VI7 is secondary dominant V/ii)

    {
        name: 'Sentimental',
        degrees: [0, { degree: 2, quality: 'dominant7', isBorrowed: true }, 3, { degree: 3, quality: 'minor', isBorrowed: true }],
        moods: ['Sad', 'Nostalgic'],
        complexity: 'Complex'
    }, // I - III7 - IV - iv (Radiohead/Creep vibe)

    {
        name: 'Gospel Lift',
        degrees: [0, 0, 3, { degree: 3, quality: 'diminished', isBorrowed: true }, 0, 4, 0],
        moods: ['Happy', 'Energetic'],
        complexity: 'Complex'
    }, // I - I7 - IV - #iv dim - I...

    // --- EXOTIC (Cinematic, Modal, Epic) ---
    { name: 'Epic Minor', degrees: [0, 5, 6], moods: ['Sad', 'Mystery', 'Epic'], complexity: 'Exotic' }, // i-VI-VII (Cinema blockbuster)
    { name: 'Dorian Vamp', degrees: [0, 3, 1, 3], moods: ['Mystery', 'Exotic'], complexity: 'Exotic' }, // i-IV-ii-IV (Santana/Fusion)
    { name: 'Space Odyssey', degrees: [0, 1], moods: ['Mystery', 'Calm'], complexity: 'Exotic' }, // I-II (Lydian)
    { name: 'Fantasy', degrees: [0, 4, 5, { degree: 3, quality: 'major', isBorrowed: true }], moods: ['Happy', 'Mystery'], complexity: 'Exotic' }, // I-V-vi-IV (Major Lydian 4)
    { name: 'Dark Hero', degrees: [0, 5, 3, { degree: 4, quality: 'minor', isBorrowed: true }], moods: ['Sad', 'Epic'], complexity: 'Exotic' }, // i-VI-iv-v (Minor v)
];

/**
 * Scales that work well for composition (avoiding overly dissonant ones)
 */
export const COMPOSER_SCALES: ScaleType[] = [
    'major',
    'minor',
    'pentatonic_major',
    'pentatonic_minor',
    'dorian',
    'lydian', // Added Lydian
    'mixolydian', // Added Mixolydian
];

/**
 * Root notes (all 7 natural notes for variety)
 */
export const COMPOSER_ROOTS: RootNote[] = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];

/**
 * Emotional vibe names for each progression - user-friendly labels
 */
export const VIBE_NAMES: Record<string, string[]> = {
    // Simple
    'Pop Anthem': ['Hopeful Dawn', 'Rising Spirit', 'Sunrise Energy', 'Bright Horizons'],
    'Classic': ['Timeless Joy', 'Gentle Breeze', 'Pure Light', 'Simple Beauty'],
    'Sad Pop': ['Midnight Reflection', 'Quiet Rain', 'Moonlit Path', 'Tender Moments'],
    'Folk': ['Forest Path', 'Wanderer\'s Song', 'Mountain Air', 'Peaceful Journey'],
    '50s Ballad': ['Vintage Love', 'Slow Dance', 'Old Radiogram', 'Memory Lane'],
    'Canon': ['Golden Thread', 'Eternal Cycle', 'Sacred Hall', 'Walking Home'],

    // Medium
    'Jazz 2-5-1': ['City Lights', 'Late Night', 'Smoky Bar', 'Velvet Lounge'],
    'Jazz Turnaround': ['Swing Step', 'Blue Note', 'Cool Breeze', 'Urban Walk'],
    'Royal Road': ['Anime Skies', 'Epic Tale', 'Heart Strings', 'Melodic Storm'],
    'Circle of Fifths': ['Infinite Loop', 'Spiral Dance', 'Returning Home', 'Cosmic Gear'],
    'Emotional Journey': ['Long Goodbye', 'Hero\'s Rest', 'Tearful Joy', 'Deep Ocean'],
    'Rising Spirit': ['Ascension', 'Climbing High', 'Victory Lap', 'New Heights'],
    'Descending Soul': ['Leaf Fall', 'Setting Sun', 'Gentle Slope', 'River Flow'],
    'Andalusian': ['Spanish Night', 'Flamenco Fire', 'Desert Mystery', 'Red Passion'],

    // Complex
    'Neo Soul Vibes': ['Silk Sheets', 'Coffee Shop', 'Liquid Gold', 'Sunday Morning'],
    'Lo-Fi Nostalgia': ['Study Beats', 'Rainy Window', 'Vinyl Crackle', 'Broken Tape'],
    'Sentimental': ['Bittersweet', 'Lost Love', 'Fade Out', 'Sepia Tone'],
    'Gospel Lift': ['Sunday Best', 'Soulful Clap', 'Higher Ground', 'Joyful Noise'],

    // Exotic
    'Epic Minor': ['Battle Cry', 'Dark Knight', 'Final Stand', 'Storm Clouds'],
    'Dorian Vamp': ['Ancient Runes', 'Mystic Cave', 'Purple Haze', 'Wandering Monk'],
    'Space Odyssey': ['Star Field', 'Lunar Base', 'Zero G', 'Orbiting Mind'],
    'Fantasy': ['Dragon Flight', 'Fairy Dust', 'Crystal Spire', 'Magic Forest'],
    'Dark Hero': ['Shadow Walker', 'Grim Resolve', 'Noir Tales', 'Silent Watcher'],
};

/**
 * Pick a random item from an array
 */
export function pickRandom<T>(arr: T[]): T {
    return arr[Math.floor(Math.random() * arr.length)];
}
